import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useShouldShowSplashScreen } from '#pplx/frontend/apps/ask/signup-ui/useShouldShowSplashScreen.js';
import { useInputActions, useShouldTriggerFocus, } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { useClientRender } from '#pplx/frontend/common/hooks/useClientRender.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useCallback, useEffect, useRef, useState } from 'react';
const MAX_FOCUS_RESTORE_FRAMES = 60;
function activeElementIsCurrentPageLink(activeElement) {
    const activeLink = activeElement.closest('a[href]');
    return activeLink instanceof HTMLAnchorElement && activeLink.href === window.location.href;
}
function canRestoreHomeFocus(focusState, shouldTriggerFocus) {
    if (shouldTriggerFocus) {
        return true;
    }
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) {
        return activeElement == null;
    }
    return (activeElement === document.body ||
        activeElement === document.documentElement ||
        !activeElement.isConnected ||
        activeElement.closest('[aria-hidden="true"]') != null ||
        activeElement.closest('[inert]') != null ||
        activeElementIsCurrentPageLink(activeElement) ||
        focusState.activeElementIsPreviousInput ||
        focusState.isFocused);
}
/**
 * Restores desktop homepage focus to the Ask input after the initial
 * configured-mode loading gate clears or home navigation remounts the input.
 *
 * AskInput's own `autofocus` only takes effect at editor mount, but the input
 * stays inert/disabled (and `autofocus` is false) until the initial configured
 * mode resolves, so mount-time autofocus is lost on the homepage. focus() is a
 * no-op while the input chrome is still `visibility:hidden`, so instead of
 * retrying on a timer, hook onto where the visibility actually changes: the
 * gate wrapper's `visibility` flip transitions on the input chrome
 * (`transition-all`), and its bubbled `transitionend` marks one moment the
 * input may become visible and focusable. SPA navigations can miss that exact
 * event, so the ready effect schedules the same bounded frame retry.
 *
 * Returns the loading-gate state and `transitionend` handler for the input
 * visibility wrapper.
 */
export function useHomeAskInputAutofocus(askInputRef) {
    const inputVisibilityRef = useRef(null);
    const [homeAutofocusReady, setHomeAutofocusReady] = useState(false);
    const { initialConfiguredModelIsLoading } = useThreadConfig();
    const hasMounted = useClientRender();
    const initialConfiguredModelResolved = hasMounted && !initialConfiguredModelIsLoading;
    const { isMobileStyle } = useIsMobile();
    const shouldShowSplashScreen = useShouldShowSplashScreen();
    const shouldTriggerFocus = useShouldTriggerFocus();
    const { setShouldTriggerFocus } = useInputActions();
    const hasRestoredFocusRef = useRef(false);
    const restoreFrameRef = useRef(null);
    const restoreAttemptRef = useRef(0);
    const shouldPrepareHomeAutofocus = initialConfiguredModelResolved && !isMobileStyle && !shouldShowSplashScreen;
    const cancelScheduledRestore = useCallback(() => {
        if (restoreFrameRef.current == null) {
            return;
        }
        window.cancelAnimationFrame(restoreFrameRef.current);
        restoreFrameRef.current = null;
    }, []);
    const scheduleFocusRestore = useCallback(() => {
        if (restoreFrameRef.current != null) {
            return;
        }
        if (hasRestoredFocusRef.current) {
            const focusState = askInputRef.current?.getFocusState();
            const canRetryFocus = focusState != null &&
                !focusState.isFocused &&
                canRestoreHomeFocus(focusState, shouldTriggerFocus);
            if (!canRetryFocus) {
                return;
            }
            hasRestoredFocusRef.current = false;
        }
        restoreAttemptRef.current = 0;
        const scheduleNextAttempt = (attempt) => {
            if (attempt < MAX_FOCUS_RESTORE_FRAMES) {
                restoreFrameRef.current = window.requestAnimationFrame(tryRestoreFocus);
            }
        };
        function tryRestoreFocus() {
            restoreFrameRef.current = null;
            const attempt = restoreAttemptRef.current + 1;
            restoreAttemptRef.current = attempt;
            const focusState = askInputRef.current?.getFocusState();
            if (focusState == null) {
                scheduleNextAttempt(attempt);
                return;
            }
            if (!focusState.isFocusable) {
                scheduleNextAttempt(attempt);
                return;
            }
            if (!canRestoreHomeFocus(focusState, shouldTriggerFocus)) {
                hasRestoredFocusRef.current = true;
                return;
            }
            if (focusState.isFocused) {
                hasRestoredFocusRef.current = true;
                if (shouldTriggerFocus) {
                    setShouldTriggerFocus(false);
                }
                return;
            }
            askInputRef.current?.focus();
            if (askInputRef.current?.getFocusState()?.isFocused) {
                hasRestoredFocusRef.current = true;
                if (shouldTriggerFocus) {
                    setShouldTriggerFocus(false);
                }
                return;
            }
            scheduleNextAttempt(attempt);
        }
        restoreFrameRef.current = window.requestAnimationFrame(tryRestoreFocus);
    }, [askInputRef, setShouldTriggerFocus, shouldTriggerFocus]);
    useEffect(() => {
        if (!shouldPrepareHomeAutofocus) {
            cancelScheduledRestore();
            setHomeAutofocusReady(false);
            hasRestoredFocusRef.current = false;
            restoreAttemptRef.current = 0;
            return;
        }
        setHomeAutofocusReady(false);
        const rafId = window.requestAnimationFrame(() => {
            setHomeAutofocusReady(true);
        });
        return () => {
            window.cancelAnimationFrame(rafId);
            cancelScheduledRestore();
        };
    }, [cancelScheduledRestore, shouldPrepareHomeAutofocus]);
    useEffect(() => {
        if (!homeAutofocusReady) {
            return;
        }
        scheduleFocusRestore();
    }, [homeAutofocusReady, scheduleFocusRestore]);
    const onGateTransitionEnd = useCallback((event) => {
        if (event.propertyName !== 'visibility' || !shouldPrepareHomeAutofocus) {
            return;
        }
        scheduleFocusRestore();
    }, [scheduleFocusRestore, shouldPrepareHomeAutofocus]);
    return {
        homeAutofocusReady,
        initialConfiguredModelResolved,
        inputVisibilityRef,
        onGateTransitionEnd,
    };
}
//# sourceMappingURL=useHomeAskInputAutofocus.js.map